import { useLocalObservable } from 'mobx-react-lite';
import { RefObject, useEffect, useRef } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import { CallBlurType } from '@gem/call/constants';

interface IBackgroundPanelPresenter {
  hasVideo: boolean;
  activeBlurType: CallBlurType | null;
  ongoingCall: Call;
  videoNode: HTMLVideoElement | null;
  playVideo(container: HTMLDivElement): void;
  stopVideo(): void;
  onBlurClick(blurType: CallBlurType): void;
  onClose(): void;
}

export const useBackgroundPanelPresenter = (): {
  presenter: IBackgroundPanelPresenter;
  videoRef: RefObject<HTMLDivElement>;
} => {
  const context = useCallContext();
  const videoRef = useRef<HTMLDivElement>(null);

  const presenter = useLocalObservable<IBackgroundPanelPresenter>(() => ({
    videoNode: null,

    get ongoingCall(): Call {
      return context.call;
    },

    get hasVideo(): boolean {
      return !presenter.ongoingCall.meStore.isVideoMuted;
    },

    get activeBlurType(): CallBlurType | null {
      return presenter.ongoingCall.meStore.blurType || null;
    },

    stopVideo(): void {
      context.callController.stopPublisherVideo(this.videoNode || undefined);
      this.videoNode = null;
    },

    playVideo(container: HTMLDivElement): void {
      this.videoNode = context.callController.playPublisherVideo(container);
    },

    onBlurClick(blurType: CallBlurType): void {
      context.callController.setBlur(blurType);
    },

    onClose(): void {
      presenter.stopVideo();
      presenter.ongoingCall.viewState.hideBackgroundPanel();
    },
  }));

  useEffect(() => {
    if (videoRef.current) {
      presenter.playVideo(videoRef.current);
    }
  }, []);

  return { presenter, videoRef };
};
